import React, { useCallback, useContext, useMemo } from "react";
import { numberFormatter } from "@common/helpers/number";
import { isEqual } from "@common/helpers/comparison";
import { Tag } from "@design-system/Tag/Tag";
import { useBulkActions } from "../../transactions/TransactionsBulkActions/useBulkActions";
import { BulkUpdateParams } from "../../transactions/TransactionsBulkActions/lib";
import { mapTransactionToSelected } from "../../transactions/TransactionsBulkActions/TransactionsBulkActionsContextProvider.lib";
import { PaginatedTransactionsDataContext } from "../../transactions/PaginatedTransactionsTable.context";
import { getPropertyLabel } from "./SimilarTransactionsUpdateDrawer.lib";
import { useSimilarTransactionsNewValue } from "./SimilarTransactionsNewValueContext";
import { SelectedTransactionsFieldButton } from "./SelectedTransactionsUpdateButtons";
import { useActionsContext } from "./ActionsCard/ActionsContext";
import styles from "./ActionsCard/ActionsCard.module.scss";

export const SelectedTransactionsCountControl: React.FC = () => {
    const bulkActions = useBulkActions();
    const { totalPages } = useContext(PaginatedTransactionsDataContext);

    if (bulkActions.selectedCount === 0) {
        return null;
    }

    const shouldShowSelectAll =
        totalPages > 1 &&
        !bulkActions.hasSelectedAll &&
        bulkActions.totalTransactions > bulkActions.selectedCount;

    return (
        <>
            <Tag
                size="lg"
                onRemove={bulkActions.clearSelection}
                removeLabel="Clear selected transactions"
            >
                {bulkActions.selectedCount} selected
            </Tag>
            {shouldShowSelectAll && (
                <button
                    type="button"
                    className={styles.selectAllTransactionsButton}
                    onClick={bulkActions.selectAll}
                >
                    Select all{" "}
                    {numberFormatter.format(bulkActions.totalTransactions)}
                </button>
            )}
        </>
    );
};

export const SelectedTransactionsFieldUpdateControl: React.FC = () => {
    const bulkActions = useBulkActions();
    const {
        transactions,
        filters: currentFilters,
        transactionCount,
    } = useContext(PaginatedTransactionsDataContext);
    const { filters: drawerFilters, upsertFilterScopedRow } =
        useActionsContext();
    const { valueToAssign, setNewValue, setBaseValue } =
        useSimilarTransactionsNewValue();

    const selectedTransactions = useMemo(
        () =>
            bulkActions.hasSelectedAll
                ? (transactions ?? []).map(mapTransactionToSelected)
                : bulkActions.selectedArray,
        [bulkActions.hasSelectedAll, bulkActions.selectedArray, transactions],
    );
    const propertyLabel = getPropertyLabel(valueToAssign.update);
    const hasFullCurrentFilterSelection =
        bulkActions.hasSelectedAll ||
        (transactionCount > 0 &&
            bulkActions.selectedCount === transactionCount);
    const hasAdditionalFilters = !isEqual(currentFilters, drawerFilters);

    const applyValue = useCallback(
        (value: BulkUpdateParams["value"], label?: string) => {
            if (hasFullCurrentFilterSelection && hasAdditionalFilters) {
                upsertFilterScopedRow({
                    value,
                    filters: currentFilters,
                    transactionCount,
                });
                for (const transaction of selectedTransactions) {
                    setNewValue(transaction.id, value, label);
                }
                return;
            }

            if (bulkActions.hasSelectedAll) {
                setBaseValue(value, label);
                return;
            }

            for (const transaction of selectedTransactions) {
                setNewValue(transaction.id, value, label);
            }
        },
        [
            hasFullCurrentFilterSelection,
            hasAdditionalFilters,
            upsertFilterScopedRow,
            currentFilters,
            transactionCount,
            bulkActions.hasSelectedAll,
            selectedTransactions,
            setBaseValue,
            setNewValue,
        ],
    );

    if (bulkActions.selectedCount === 0 || selectedTransactions.length === 0) {
        return null;
    }

    return (
        <SelectedTransactionsFieldButton
            update={valueToAssign.update}
            propertyLabel={propertyLabel}
            selectedTransactions={selectedTransactions}
            applyValue={applyValue}
        />
    );
};
